import { useState, useEffect, useRef } from 'react';
import { CircularProgress, Modal, Tooltip, Paper, Tab, Tabs, Box, ToggleButton, ToggleButtonGroup, Switch, FormControlLabel } from '@mui/material';
import { Close, Edit, Check, History, SwapHoriz, FilterList, ArrowUpward, ArrowDownward, ArrowForward } from '@mui/icons-material';
import Select from 'react-select';
import { twMerge } from 'tailwind-merge';
import { useDockData, DockDoor, Status, SpotGroup, HistoryEntry, DockHistory } from './dockDataFetch';
import axios from 'axios';
import { useYardData } from '../YardManagement/yardDataFetch';

type DoorAction = {
  label: string | ((door: DockDoor) => string);
  color: string;
  icon: React.ReactNode;
  apiEndpoint: string;
  showWhen: (door: DockDoor) => boolean;
};

const doorActions: DoorAction[] = [
  {
    label: 'Send to Yard',
    color: 'bg-blue-500 hover:bg-blue-600',
    icon: <SwapHoriz fontSize="small" className="mr-1" />,
    apiEndpoint: '/api/v1/admin/drivers/send_to_yard',
    showWhen: (door) => door.current_status === 'ready_for_yard'
  },
  {
    label: 'Safety Check Complete',
    color: 'bg-green-500 hover:bg-green-600',
    icon: <Check fontSize="small" className="mr-1" />,
    apiEndpoint: `${process.env.REACT_APP_WRITE_API_URL}/api/v1/admin/drivers/update_loading_status`,
    showWhen: (door) => door.current_status === 'needs safety check'
  },
  {
    label: (door) => door.group === 'inbound' ? 'Start Unloading' : 'Start Loading',
    color: 'bg-blue-500 hover:bg-blue-600',
    icon: <ArrowForward fontSize="small" className="mr-1" />,
    apiEndpoint: `${process.env.REACT_APP_WRITE_API_URL}/api/v1/admin/drivers/update_loading_status`,
    showWhen: (door) => door.current_status === 'safety check complete'
  },
  {
    label: 'Loading Complete',
    color: 'bg-green-500 hover:bg-green-600',
    icon: <Check fontSize="small" className="mr-1" />,
    apiEndpoint: `${process.env.REACT_APP_WRITE_API_URL}/api/v1/admin/drivers/update_loading_status`,
    showWhen: (door) => door.current_status === 'loading started'
  },
  {
    label: 'Unloading Complete',
    color: 'bg-green-500 hover:bg-green-600',
    icon: <Check fontSize="small" className="mr-1" />,
    apiEndpoint: `${process.env.REACT_APP_WRITE_API_URL}/api/v1/admin/drivers/update_loading_status`,
    showWhen: (door) => door.current_status === 'unloading started'
  }
  // Add more actions as needed
];

const DockManagement = () => {
  // Get real dock data from API using our custom hook
  const {
    loading: apiLoading,
    dockDoors: apiDockDoors,
    dockHistory: apiDockHistory,
    error,
    facilities,
    selectedFacility,
    setSelectedFacility,
    timeInterval,
    setTimeInterval
  } = useDockData();

  // Set time interval to max always
  useEffect(() => {
    setTimeInterval(8760); // Always set to max (1 year)
  }, [setTimeInterval]);

  // Local state
  const [selectedDoor, setSelectedDoor] = useState<DockDoor | null>(null);
  const [showDoorDetailsModal, setShowDoorDetailsModal] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [groupFilter, setGroupFilter] = useState<SpotGroup | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistoryDoor, setSelectedHistoryDoor] = useState<DockDoor | null>(null);
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month'>('today');
  const [showYardMoveModal, setShowYardMoveModal] = useState(false);
  const [selectedYardSpot, setSelectedYardSpot] = useState<string>('');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [colorConfig, setColorConfig] = useState({
    mdt: {
      warning: '#FFEDD5', // orange-100
      critical: '#FEE2E2', // red-100
      good: '#D1FAE5',    // green-100
      warningThreshold: 60, // minutes
      criticalThreshold: 45, // minutes
      textColor: '#000000', // black
    },
    detention: {
      warning: '#FFEDD5', // orange-100
      critical: '#FEE2E2', // red-100
      good: '#D1FAE5',    // green-100
      warningThreshold: 60, // minutes
      criticalThreshold: 45, // minutes
      textColor: '#000000', // black
    },
    dockTimer: {
      warning: '#FFEDD5', // orange-100
      critical: '#FEE2E2', // red-100
      good: '#D1FAE5',    // green-100
      warningThreshold: 45, // minutes
      criticalThreshold: 90, // minutes
      textColor: '#000000', // black
    },
    card: {
      default: '#FFFFFF',   // white (default)
      oneWarning: '#FFFBEB', // yellow-50
      twoWarnings: '#FEF3C7', // yellow-100
      threeWarnings: '#FDE68A', // yellow-200
      oneCritical: '#FEF2F2', // red-50
      twoCriticals: '#FEE2E2', // red-100
      threeCriticals: '#FECACA', // red-200
      mixed: '#FDF2F8',     // pink-50 (for mixed warning/critical)
      textColor: '#000000', // black
    }
  });
  
  // Add a new state variable for the modal tabs
  const [configModalTab, setConfigModalTab] = useState(0);
  
  // Sorting state
  const [sortCriteria, setSortCriteria] = useState<'number' | 'status' | 'mdt' | 'detention' | 'dockTimer'>('number');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Open door details modal
  const handleDoorClick = (door: DockDoor) => {
    setShowDoorDetailsModal(true);
    setSelectedDoor(door);
    setNoteValue(door.note || '');
    setIsEditingNote(false);
  };
  
  // Close door details modal
  const handleCloseDetailsModal = () => {
    setShowDoorDetailsModal(false);
    setSelectedDoor(null);
  };
  
  // Handle status change
  const handleStatusChange = (status: Status) => {
    if (!selectedDoor) return;
    
    // TODO: Implement API call to update status
    console.log('Update status:', status);
  };
  
  // Handle group change
  const handleGroupChange = (group: SpotGroup) => {
    if (!selectedDoor) return;
    
    // TODO: Implement API call to update group
    console.log('Update group:', group);
  };
  
  // Open history modal
  const handleShowHistory = (door: DockDoor) => {
    setSelectedHistoryDoor(door);
    setShowHistoryModal(true);
  };
  
  // Close history modal
  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedHistoryDoor(null);
  };
  
  // Get color based on status
  const getStatusColor = (status: Status): string => {
    switch (status) {
      case 'empty':
        return 'bg-gray-500';
      case 'loading':
        return 'bg-yellow-500';
      case 'loaded':
        return 'bg-green-500';
      case 'unloading':
        return 'bg-blue-500';
      case 'unloaded':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };
  
  // Get color based on group
  const getGroupColor = (group: SpotGroup): string => {
    switch (group) {
      case 'inbound':
        return 'border-blue-500 border-4';
      case 'outbound':
        return 'border-orange-500 border-4';
      default:
        return 'border-gray-400 border';
    }
  };
  
  // Utility function to extract minutes from timer string (e.g., "01:30:00" -> 90)
  const getMinutesFromTimer = (timeString: string): number => {
    if (!timeString) return 0;
    
    // Handle negative times (starting with -)
    const isNegative = timeString.startsWith('-');
    const cleanTimeString = isNegative ? timeString.substring(1) : timeString;
    
    const parts = cleanTimeString.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0], 10) || 0;
      const minutes = parseInt(parts[1], 10) || 0;
      // If seconds are included, handle them too
      const seconds = parts.length > 2 ? (parseInt(parts[2], 10) || 0) / 60 : 0;
      
      return (hours * 60 + minutes + seconds) * (isNegative ? -1 : 1);
    }
    
    return 0;
  };

  // Sort dock doors based on the selected criteria and direction
  const getSortedDockDoors = (doors: DockDoor[]): DockDoor[] => {
    return [...doors].sort((a, b) => {
      switch (sortCriteria) {
        case 'number':
          // Sort by door number
          const numA = parseInt(a.number.replace(/\D/g, ''), 10) || 0;
          const numB = parseInt(b.number.replace(/\D/g, ''), 10) || 0;
          return sortDirection === 'asc' ? numA - numB : numB - numA;
        
        case 'status':
          // Sort by most severe status first (based on timer statuses)
          const aWarnings = [
            getTimeStatus(a.mdt || '00:00:00', 'mdt'), 
            getTimeStatus(a.detention || '00:00:00', 'detention'),
            getTimeStatus(a.dock_timer || '00:00:00', 'dockTimer')
          ].filter(s => s === 'orange').length;
          
          const aCriticals = [
            getTimeStatus(a.mdt || '00:00:00', 'mdt'), 
            getTimeStatus(a.detention || '00:00:00', 'detention'),
            getTimeStatus(a.dock_timer || '00:00:00', 'dockTimer')
          ].filter(s => s === 'red').length;
          
          const bWarnings = [
            getTimeStatus(b.mdt || '00:00:00', 'mdt'), 
            getTimeStatus(b.detention || '00:00:00', 'detention'),
            getTimeStatus(b.dock_timer || '00:00:00', 'dockTimer')
          ].filter(s => s === 'orange').length;
          
          const bCriticals = [
            getTimeStatus(b.mdt || '00:00:00', 'mdt'), 
            getTimeStatus(b.detention || '00:00:00', 'detention'),
            getTimeStatus(b.dock_timer || '00:00:00', 'dockTimer')
          ].filter(s => s === 'red').length;
          
          // Compare criticals first, then warnings
          if (aCriticals !== bCriticals) {
            return sortDirection === 'desc' ? bCriticals - aCriticals : aCriticals - bCriticals;
          }
          return sortDirection === 'desc' ? bWarnings - aWarnings : aWarnings - bWarnings;
          
        case 'mdt':
          // For MDT and detention, negative values are worse
          const mdtA = a.mdt?.startsWith('-') ? -getMinutesFromTimer(a.mdt) : getMinutesFromTimer(a.mdt);
          const mdtB = b.mdt?.startsWith('-') ? -getMinutesFromTimer(b.mdt) : getMinutesFromTimer(b.mdt);
          return sortDirection === 'desc' ? mdtA - mdtB : mdtB - mdtA; // Descending sorts worst first
          
        case 'detention':
          const detA = a.detention?.startsWith('-') ? -getMinutesFromTimer(a.detention) : getMinutesFromTimer(a.detention);
          const detB = b.detention?.startsWith('-') ? -getMinutesFromTimer(b.detention) : getMinutesFromTimer(b.detention);
          return sortDirection === 'desc' ? detA - detB : detB - detA; // Descending sorts worst first
          
        case 'dockTimer':
          // For dock timer, higher values are worse
          const dockA = getMinutesFromTimer(a.dock_timer);
          const dockB = getMinutesFromTimer(b.dock_timer);
          return sortDirection === 'desc' ? dockB - dockA : dockA - dockB; // Descending sorts worst first
          
        default:
          return 0;
      }
    });
  };
  
  // Update the filteredDockDoors logic to include sorting
  const filteredAndSortedDockDoors = getSortedDockDoors(
    apiDockDoors.filter(door => {
      if (groupFilter !== 'all' && door.group !== groupFilter) return false;
      if (statusFilter !== 'all' && door.status !== statusFilter) return false;
      return true;
    })
  );
  
  // Doors with active assignments
  const activeDoors = apiDockDoors.filter(door => 
    door.status === 'loading' || door.status === 'unloading'
  );
  
  // Upcoming schedule based on scheduled times
  const upcomingSchedules = apiDockDoors.flatMap(door => 
    door.scheduledTimes.map(time => ({
      doorId: door.id,
      doorNumber: door.number,
      ...time
    }))
  ).sort((a, b) => a.start.localeCompare(b.start));
  
  // Add new helper functions at the top level
  const getTimeStatus = (timeString: string, timerType: 'mdt' | 'detention' | 'dockTimer'): 'red' | 'green' | 'orange' => {
    console.log(timeString);
    console.log(timerType);
    
    if (!timeString) return 'green';
    
    // Extract minutes from timestring
    const matches = timeString.match(/\d+/g);
    if (matches && matches.length >= 2) {
      const hours = parseInt(matches[0]);
      const mins = parseInt(matches[1]);
      const totalMinutes = hours * 60 + mins;
      console.log(totalMinutes);

      // if all 0s return green
      if (totalMinutes === 0) return 'green';

      if (timerType === 'mdt' || timerType === 'detention') {
        // if first chracter is - then make it red
        if (timeString.startsWith('-')) {
          return 'red';
        }
        // For MDT and detention, LARGER minutes are good (more time remaining)
        // So warning is when time gets low, critical is when time gets very low
        if (totalMinutes < colorConfig[timerType].criticalThreshold) return 'red';
        if (totalMinutes < colorConfig[timerType].warningThreshold) return 'orange';
      } else if (timerType === 'dockTimer') {
        // For dock timer, SMALLER minutes are good (less time spent)
        // So warning is when time gets high, critical when time gets very high
        if (totalMinutes > colorConfig[timerType].criticalThreshold) return 'red';
        if (totalMinutes > colorConfig[timerType].warningThreshold) return 'orange';
      }
      
      return 'green';
    }
    
    return 'green';
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  const handleDoorAction = async (door: DockDoor, action: DoorAction) => {
    // add a confirm box
    const confirm = window.confirm('Are you sure you want to perform this action?');
    if (!confirm) return;
    if (!door.shipmentNumber) return;
    
    try {
      await axios.post(action.apiEndpoint, {
        shipment_id: door.id
      });
      
      // Force a refresh by incrementing the time interval
      setTimeInterval(prev => prev + 1);
    } catch (error) {
      console.error('Failed to update door status:', error);
    }
  };

  // Inside the DockManagement component, add this with other hooks
  const {
    yardSpots,
    loading: yardLoading
  } = useYardData(selectedFacility?.id);

  // Function to save configuration
  const saveConfiguration = async () => {
    try {
      // TODO: Implement actual API call
      console.log('Saving configuration:', colorConfig);
      // await axios.post('/api/v1/admin/configuration/dock', colorConfig);
      setShowConfigModal(false);
    } catch (error) {
      console.error('Failed to save configuration:', error);
    }
  };

  // Function to get timer background color based on status and configuration
  const getTimerBackgroundColor = (type: 'mdt' | 'detention' | 'dockTimer', status: 'red' | 'green' | 'orange') => {
    if (status === 'red') return colorConfig[type].critical;
    if (status === 'orange') return colorConfig[type].warning;
    return colorConfig[type].good;
  };

  // Function to get card background color based on status indicators
  const getCardBackgroundColor = (door: DockDoor) => {
    // If door has no PUID, it's empty - show as green
    if (!door.puid) {
      return '#D1FAE5'; // green-100
    }

    // Count warning and critical indicators
    const mdtStatus = getTimeStatus(door.mdt || '00:00:00', 'mdt');
    const detentionStatus = getTimeStatus(door.detention || '00:00:00', 'detention');
    const dockTimerStatus = getTimeStatus(door.dock_timer || '00:00:00', 'dockTimer');
    
    const warningCount = [mdtStatus, detentionStatus, dockTimerStatus].filter(status => status === 'orange').length;
    const criticalCount = [mdtStatus, detentionStatus, dockTimerStatus].filter(status => status === 'red').length;
    
    // Determine card color based on counts
    if (criticalCount > 0) {
      if (warningCount > 0) {
        // Mixed warning and critical
        return colorConfig.card.mixed;
      } else {
        // Only critical indicators
        if (criticalCount === 1) return colorConfig.card.oneCritical;
        if (criticalCount === 2) return colorConfig.card.twoCriticals;
        if (criticalCount === 3) return colorConfig.card.threeCriticals;
      }
    } else if (warningCount > 0) {
      // Only warning indicators
      if (warningCount === 1) return colorConfig.card.oneWarning;
      if (warningCount === 2) return colorConfig.card.twoWarnings;
      if (warningCount === 3) return colorConfig.card.threeWarnings;
    }
    
    // Default case (all good)
    return colorConfig.card.default;
  };

  // Add a handler for the modal tabs
  const handleConfigModalTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setConfigModalTab(newValue);
  };

  const [noteValue, setNoteValue] = useState<string>('');
  const [isEditingNote, setIsEditingNote] = useState(false);

  const handleSaveNote = async () => {
    if (!selectedDoor || !selectedDoor.shipmentNumber) return;
    
    try {
      const payload = {
        shipment_id: selectedDoor.id,
        note: noteValue
      };
      
      const response = await axios.post(
        `${process.env.REACT_APP_WRITE_API_URL}/api/v1/admin/drivers/add_note`, 
        payload
      );
      
      console.log('Add note response:', response.data);
      
      // Success message
      if (response.data && response.data.message) {
        // Optional: show success message
        // alert(response.data.message);
      }
      
      // Update the selected door with the new note
      const updatedDoor = {...selectedDoor, note: noteValue};
      setSelectedDoor(updatedDoor);
      
      // Refresh data
      setTimeInterval(prev => prev + 1);
      
      // Exit edit mode
      setIsEditingNote(false);
    } catch (error: any) {
      console.error('Failed to save note:', error);
      
      // Show error message from API if available
      if (error.response && error.response.data && error.response.data.error) {
        alert(`Error: ${error.response.data.error}`);
      } else {
        alert('Failed to save note. Please try again.');
      }
    }
  };

  // Add tiny mode state with localStorage persistence
  const [isTinyMode, setIsTinyMode] = useState(() => {
    const saved = localStorage.getItem('dockTinyMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Add state for tracking expanded card
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  
  // Save tiny mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('dockTinyMode', JSON.stringify(isTinyMode));
  }, [isTinyMode]);

  if (apiLoading) {
    return (
      <div className="flex flex-col items-center mt-4 w-full">
        <CircularProgress />
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 text-red-500">
        <h1 className="font-bold text-xl mb-3">Error</h1>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="shadow bg-[#FBFBFB] p-3 mb-5">
        <div className="flex flex-col md:flex-row gap-3 mb-3">
          <div>
            <h1 className="font-bold text-xl mb-3">Dock Management</h1>
            <p>Monitor and manage dock doors, view current status and historical usage.</p>
          </div>
          <div className="flex gap-3 ml-auto items-center">
            <FormControlLabel
              control={
                <Switch
                  checked={isTinyMode}
                  onChange={(e) => setIsTinyMode(e.target.checked)}
                  color="primary"
                />
              }
              label="Compact View"
            />
            <Select
              options={facilities.map(facility => ({
                value: facility,
                label: `(${facility.id}) ${facility.name}`
              }))}
              isSearchable={true}
              isClearable
              value={facilities.map(facility => ({
                value: facility,
                label: `(${facility.id}) ${facility.name}`
              })).find(option => option.value.id === selectedFacility?.id)}
              placeholder="Facilities"
              onChange={(selectedOption: any) => {
                setSelectedFacility(selectedOption?.value);
              }}
              className="w-60"
            />
            <div className="w-40 flex items-center justify-center border rounded px-3 py-2 bg-gray-50">
              <span className="text-sm font-medium">Time: Max</span>
            </div>
            <button 
              className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium flex items-center"
              onClick={() => setShowConfigModal(true)}
            >
              <Edit fontSize="small" className="mr-1" />
              Configure
            </button>
          </div>
        </div>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Current Status" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <div className="flex flex-col gap-5">
          {/* Filters Row */}
          <div className="flex flex-wrap gap-3 items-center bg-white p-3 shadow">
            <div className="flex items-center">
              <FilterList className="mr-2 text-gray-500" />
              <span className="font-medium mr-2">Filters:</span>
            </div>
            
            <div className="flex items-center mr-4">
              <span className="mr-2">Group:</span>
              <Select
                options={[
                  { value: 'all', label: 'All Groups' },
                  { value: 'inbound', label: 'Inbound' },
                  { value: 'outbound', label: 'Outbound' },
                  { value: 'unassigned', label: 'Unassigned' }
                ]}
                defaultValue={{ value: 'all', label: 'All Groups' }}
                onChange={(option: any) => setGroupFilter(option.value)}
                className="w-40"
              />
            </div>
            
            {/* <div className="flex items-center mr-4">
              <span className="mr-2">Status:</span>
              <Select
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'empty', label: 'Empty' },
                  { value: 'loading', label: 'Loading' },
                  { value: 'loaded', label: 'Loaded' },
                  { value: 'unloading', label: 'Unloading' },
                  { value: 'unloaded', label: 'Unloaded' }
                ]}
                defaultValue={{ value: 'all', label: 'All Status' }}
                onChange={(option: any) => setStatusFilter(option.value)}
                className="w-40"
              />
            </div> */}
            
            {/* Sort Options */}
            <div className="flex items-center mr-4">
              <span className="mr-2">Sort By:</span>
              <Select
                options={[
                  { value: 'number', label: 'Door Number' },
                  { value: 'mdt', label: 'MDT' },
                  { value: 'detention', label: 'Detention Time' },
                  { value: 'dockTimer', label: 'Dock Timer' }
                ]}
                value={{ 
                  value: sortCriteria, 
                  label: sortCriteria === 'number' ? 'Door Number' : 
                         
                         sortCriteria === 'mdt' ? 'MDT' :
                         sortCriteria === 'detention' ? 'Detention Time' : 'Dock Timer'
                }}
                onChange={(option: any) => setSortCriteria(option.value)}
                className="w-40"
              />
            </div>
            
            <button
              className="p-2 rounded border flex items-center bg-gray-50 hover:bg-gray-100"
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            >
              {sortDirection === 'asc' ? (
                <>
                  <ArrowUpward fontSize="small" className="mr-1" />
                  <span>Ascending</span>
                </>
              ) : (
                <>
                  <ArrowDownward fontSize="small" className="mr-1" />
                  <span>Descending</span>
                </>
              )}
            </button>
          </div>
          
          {/* Dock Door Grid */}
          <div className={`grid ${isTinyMode ? 'grid-cols-8 sm:grid-cols-12 md:grid-cols-16 lg:grid-cols-20' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'} gap-2`}>
            {filteredAndSortedDockDoors.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                No dock doors match your filters
              </div>
            ) : (
              filteredAndSortedDockDoors.map(door => {
                const isExpanded = expandedCardId === door.id;

                if (isTinyMode && isExpanded) {
                  // Expanded tiny card - Takes up more space in the grid but stays within the flow
                  return (
                    <div 
                      key={door.id}
                      className="col-span-3 row-span-3 border rounded shadow-lg overflow-hidden flex flex-col h-auto bg-white z-10"
                      style={{ 
                        color: colorConfig.card.textColor 
                      }}
                    >
                      {/* Door Header */}
                      <div className="flex justify-between items-center p-3 border-b">
                        <h3 className="font-bold">Door {door.number}</h3>
                        <div className="flex items-center gap-2">
                          {/* Close button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); 
                              setExpandedCardId(null); 
                            }}
                            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                          >
                            <Close fontSize="small"/>
                          </button>
                          <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            {door.current_status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      
                      {/* Status Boxes */}
                      <div className="grid grid-cols-3 gap-1 p-2 border-b">
                        {/* MDT Box */}
                        <div style={{ 
                          backgroundColor: getTimerBackgroundColor('mdt', getTimeStatus(door.mdt || '00:00:00', 'mdt')),
                          color: colorConfig.mdt.textColor,
                          border: 'solid black 1px'
                        }} className="p-2 rounded">
                          <div className="text-xs font-medium mb-1">MDT</div>
                          <div className="text-sm">{door.mdt || '00:00:00'}</div>
                        </div>

                        {/* Dock Timer Box */}
                        <div style={{ 
                          backgroundColor: getTimerBackgroundColor('dockTimer', getTimeStatus(door.dock_timer || '00:00:00', 'dockTimer')),
                          color: colorConfig.dockTimer.textColor,
                          border: 'solid black 1px'
                        }} className="p-2 rounded">
                          <div className="text-xs font-medium mb-1">Dock Timer</div>
                          <div className="text-sm">{door.dock_timer || '00:00:00'}</div>
                        </div>

                        {/* Detention Time Box */}
                        <div style={{ 
                          backgroundColor: getTimerBackgroundColor('detention', getTimeStatus(door.detention || '00:00:00', 'detention')),
                          color: colorConfig.detention.textColor,
                          border: 'solid black 1px'
                        }} className="p-2 rounded">
                          <div className="text-xs font-medium mb-1">Det. time</div>
                          <div className="text-sm">{door.detention || '00:00:00'}</div>
                        </div>
                      </div>
                      
                      {/* Door Body */}
                      <div className="p-3">
                        <div className="flex justify-between mb-2">
                          <div className={`text-xs px-2 py-1 rounded ${
                            door.group === 'inbound' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {door.group === 'inbound' ? 'INBOUND' : door.group === 'outbound' ? 'OUTBOUND' : ''}
                          </div>
                        </div>
                        
                        {(door.status === 'loading' || door.status === 'unloading' || door.trailerNumber || door.shipmentNumber) && (
                          <div className="mt-2 border-t pt-2">
                            <div className="flex">
                              <div className="flex-1 pr-2">
                                <div className="flex items-center mb-1">
                                  <span className="text-sm font-medium mr-1">Driver:</span>
                                  <span className="text-sm">{door.driverName || "No Driver"}</span>
                                </div>
                                {door.carrier_name && ( <div className="flex items-center mb-1"> <span className="text-sm font-medium mr-1">SCAC:</span> <span className="text-sm">{door.carrier_name}</span> </div> )}
                                {door.trailerNumber && ( <div className="flex items-center mb-1"> <span className="text-sm font-medium mr-1">Trailer:</span> <span className="text-sm">{door.trailerNumber}</span> </div> )}
                                {door.shipmentNumber && ( <div className="flex items-center"> <span className="text-sm font-medium mr-1">Shipment:</span> <span className="text-sm">{door.shipmentNumber}</span> </div> )}
                              </div>
                              <div className="flex-1 pl-2 border-l flex flex-col">
                                {door.appointment_date && ( <div className="mb-1"> <span className="text-sm font-medium mr-1">Appt Time:</span> <span className="text-sm text-gray-600">{door.appointment_date.split('.')[0].slice(5, -3)}</span> </div> )}
                                {door.note && ( <div className="flex flex-col flex-grow"> <div className="mt-1 text-sm text-gray-600 italic overflow-auto max-h-20 flex-grow bg-yellow-50 p-1 rounded">{door.note}</div> </div> )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {door.scheduledTimes.length > 0 && (
                          <div className="mt-3 border-t pt-3">
                            <h4 className="font-medium text-sm mb-1">Next Scheduled:</h4>
                            <p className="text-sm">{door.scheduledTimes[0].start} - {door.scheduledTimes[0].end}</p>
                            <p className="text-xs text-gray-600">{door.scheduledTimes[0].shipmentId}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Actions Footer */}
                      <div className="bg-gray-50 p-2 flex flex-col gap-2 mt-auto">
                        <div className="flex gap-2">
                          {door.bol_id !== null && (
                              <button 
                              className="flex-0.5 bg-purple-500 hover:bg-purple-600 text-white text-xs py-1 px-2 rounded flex items-center justify-center"
                              onClick={(e) => { e.stopPropagation(); setSelectedDoor(door); setShowYardMoveModal(true); }} >
                              <SwapHoriz fontSize="small" className="mr-1" /> Move To Yard </button>
                          )}
                          {doorActions.map((action) => 
                            action.showWhen(door) && (
                              <button 
                                key={typeof action.label === 'function' ? action.label(door) : action.label}
                                className={`flex-1 ${action.color} text-white text-xs py-1 px-2 rounded flex items-center justify-center`}
                                onClick={(e) => { e.stopPropagation(); handleDoorAction(door, action); }} >
                                {action.icon} {typeof action.label === 'function' ? action.label(door) : action.label}
                              </button>
                            )
                          )}
                        </div>
                        <div className="flex justify-end">
                          <button 
                            className="text-blue-600 hover:text-blue-800 text-xs flex items-center"
                            onClick={(e) => { 
                                e.stopPropagation(); 
                                setSelectedDoor(door);
                                setNoteValue(door.note || '');
                                setIsEditingNote(false);
                                setShowDoorDetailsModal(true);
                            }}
                          >
                            <Edit fontSize="small" className="mr-1" /> Details Modal
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                } else if (isTinyMode) {
                  // Regular tiny card (not expanded)
                  return (
                    <Tooltip 
                      key={door.id}
                      title={`Door ${door.number} - ${door.current_status}`}
                      arrow
                      placement="top"
                    >
                      <div 
                        className="aspect-square cursor-pointer flex flex-col items-center justify-center"
                        style={{ 
                          backgroundColor: getCardBackgroundColor(door),
                          border: door.group === 'inbound' ? '2px solid #3B82F6' : 
                                 door.group === 'outbound' ? '2px solid #F97316' : 
                                 '1px solid #9CA3AF'
                        }}
                        onClick={() => setExpandedCardId(door.id)}
                      >
                        <div className="text-xs font-bold">
                          {door.number}
                        </div>
                        <div className="text-[10px] font-bold mt-1 text-center px-1">
                          {door.current_status.toUpperCase()}
                        </div>
                      </div>
                    </Tooltip>
                  );
                } else {
                  // Regular card in normal (non-tiny) mode
                  return (
                    <div 
                      key={door.id} 
                      className={`border rounded shadow-sm overflow-hidden flex flex-col h-full cursor-pointer`}
                      style={{ 
                        backgroundColor: getCardBackgroundColor(door),
                        color: colorConfig.card.textColor 
                      }}
                      onClick={() => { 
                        setSelectedDoor(door);
                        setNoteValue(door.note || '');
                        setIsEditingNote(false);
                        setShowDoorDetailsModal(true); 
                      }}
                    >
                    {/* Normal card content - unchanged */}
                    {/* Door Header */}
                      <div className={`flex justify-between items-center p-3 `}>
                        <h3 className="font-bold">Door {door.number}</h3>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800`}>
                            {door.current_status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      {/* Status Boxes */}
                      <div className="grid grid-cols-3 gap-1 p-2 border-b">
                         {/* MDT Box */}
                        <div style={{ 
                          backgroundColor: getTimerBackgroundColor('mdt', getTimeStatus(door.mdt || '00:00:00', 'mdt')),
                          color: colorConfig.mdt.textColor,
                          border: 'solid black 1px'
                        }} className="p-2 rounded">
                          <div className="text-xs font-medium mb-1">MDT</div>
                          <div className="text-sm">{door.mdt || '00:00:00'}</div>
                        </div>
                         {/* Dock Timer Box */}
                        <div style={{ 
                          backgroundColor: getTimerBackgroundColor('dockTimer', getTimeStatus(door.dock_timer || '00:00:00', 'dockTimer')),
                          color: colorConfig.dockTimer.textColor ,
                          border: 'solid black 1px'
                        }} className="p-2 rounded">
                          <div className="text-xs font-medium mb-1">Dock Timer</div>
                          <div className="text-sm">{door.dock_timer || '00:00:00'}</div>
                        </div>
                         {/* Detention Time Box */}
                        <div style={{ 
                          backgroundColor: getTimerBackgroundColor('detention', getTimeStatus(door.detention || '00:00:00', 'detention')),
                          color: colorConfig.detention.textColor,
                          border: 'solid black 1px'
                        }} className="p-2 rounded">
                          <div className="text-xs font-medium mb-1">Det. time</div>
                          <div className="text-sm">{door.detention || '00:00:00'}</div>
                        </div>
                      </div>
                      {/* Door Body */}
                      <div className="p-3">
                        <div className="flex justify-between mb-2">
                          <div className={`text-xs px-2 py-1 rounded ${ door.group === 'inbound' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800' }`}>
                            {door.group === 'inbound' ? 'INBOUND' : door.group === 'outbound' ? 'OUTBOUND' : ''}
                          </div>
                        </div>
                        {(door.status === 'loading' || door.status === 'unloading' || door.trailerNumber || door.shipmentNumber) && (
                          <div className="mt-2 border-t pt-2">
                            <div className="flex">
                              <div className="flex-1 pr-2">
                                <div className="flex items-center mb-1"><span className="text-sm font-medium mr-1">Driver:</span><span className="text-sm">{door.driverName || "No Driver"}</span></div>
                                {door.carrier_name && (<div className="flex items-center mb-1"><span className="text-sm font-medium mr-1">SCAC:</span><span className="text-sm">{door.carrier_name}</span></div>)}
                                {door.trailerNumber && (<div className="flex items-center mb-1"><span className="text-sm font-medium mr-1">Trailer:</span><span className="text-sm">{door.trailerNumber}</span></div>)}
                                {door.shipmentNumber && (<div className="flex items-center"><span className="text-sm font-medium mr-1">Shipment:</span><span className="text-sm">{door.shipmentNumber}</span></div>)}
                              </div>
                              <div className="flex-1 pl-2 border-l flex flex-col">
                                {door.appointment_date && (<div className="mb-1"><span className="text-sm font-medium mr-1">Appt Time:</span><span className="text-sm text-gray-600">{door.appointment_date.split('.')[0].slice(5, -3)}</span></div>)}
                                {door.note && (<div className="flex flex-col flex-grow"><div className="mt-1 text-sm text-gray-600 italic overflow-auto max-h-20 flex-grow bg-yellow-50 p-1 rounded">{door.note}</div></div>)}
                              </div>
                            </div>
                          </div>
                        )}
                        {door.scheduledTimes.length > 0 && (
                          <div className="mt-3 border-t pt-3">
                            <h4 className="font-medium text-sm mb-1">Next Scheduled:</h4>
                            <p className="text-sm">{door.scheduledTimes[0].start} - {door.scheduledTimes[0].end}</p>
                            <p className="text-xs text-gray-600">{door.scheduledTimes[0].shipmentId}</p>
                          </div>
                        )}
                      </div>
                      {/* Actions Footer */}
                      <div className="bg-gray-50 p-2 flex flex-col gap-2 mt-auto">
                        <div className="flex gap-2">
                          {door.bol_id !== null && ( <button className="flex-0.5 bg-purple-500 hover:bg-purple-600 text-white text-xs py-1 px-2 rounded flex items-center justify-center" onClick={(e) => { e.stopPropagation(); setSelectedDoor(door); setShowYardMoveModal(true); }} > <SwapHoriz fontSize="small" className="mr-1" /> Move To Yard </button> )}
                          {doorActions.map((action) => action.showWhen(door) && ( <button key={typeof action.label === 'function' ? action.label(door) : action.label} className={`flex-1 ${action.color} text-white text-xs py-1 px-2 rounded flex items-center justify-center`} onClick={(e) => { e.stopPropagation(); handleDoorAction(door, action); }} > {action.icon} {typeof action.label === 'function' ? action.label(door) : action.label} </button> ))}
                        </div>
                        <div className="flex justify-end">
                          <button className="text-blue-600 hover:text-blue-800 text-xs flex items-center" onClick={(e) => { e.stopPropagation(); setSelectedDoor(door); setNoteValue(door.note || ''); setIsEditingNote(false); setShowDoorDetailsModal(true); }} > <Edit fontSize="small" className="mr-1" /> Details Modal </button>
                        </div>
                      </div>
                    </div>
                  );
                }
              })
            )}
          </div>
          
        
        </div>
      )}

      {tabValue === 1 && (
        <div className="flex flex-col gap-5">
          {/* History Filters */}
          <div className="flex flex-wrap gap-3 items-center bg-white p-3 shadow">
            <div className="flex items-center">
              <FilterList className="mr-2 text-gray-500" />
              <span className="font-medium mr-2">Time Range:</span>
            </div>
            
            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={(event, newTimeRange) => {
                if (newTimeRange !== null) {
                  setTimeRange(newTimeRange);
                }
              }}
              size="small"
            >
              <ToggleButton value="today">Today</ToggleButton>
              <ToggleButton value="week">Week</ToggleButton>
              <ToggleButton value="month">Month</ToggleButton>
            </ToggleButtonGroup>
            
            <div className="flex items-center ml-auto">
              <span className="mr-2">Door:</span>
              <Select
                options={[
                  { value: 'all', label: 'All Doors' },
                  ...apiDockDoors.map((door: DockDoor) => ({
                    value: door.id,
                    label: `Door ${door.number}`
                  }))
                ]}
                defaultValue={{ value: 'all', label: 'All Doors' }}
                className="w-40"
              />
            </div>
          </div>
          
          {/* Activity Timeline */}
          <div className="bg-white p-4 shadow">
            <h2 className="font-bold text-lg mb-4">Activity Timeline</h2>
            
            <div className="relative overflow-x-auto" style={{ height: '300px' }}>
              {/* Time markers */}
              <div className="absolute top-0 left-0 right-0 flex border-b">
                {Array.from({ length: 12 }, (_, i) => (
                  <div key={i} className="flex-1 text-xs text-center border-r py-1">
                    {(i + 8) % 12 === 0 ? '12' : (i + 8) % 12}:00 {i + 8 < 12 ? 'AM' : 'PM'}
                  </div>
                ))}
              </div>
              
              {/* Swim lanes for dock doors */}
              <div className="mt-8">
                {apiDockDoors.slice(0, 10).map((door, index) => {
                  const doorHistory = apiDockHistory[door.id] || [];
                  
                  return (
                    <div key={door.id} className="flex items-center h-10">
                      <div className="w-16 text-xs font-bold">
                        Door {door.number}
                      </div>
                      <div className="flex-1 relative h-6 bg-gray-100">
                        {doorHistory.map((entry, entryIndex) => {
                          // For demo purposes, position entries evenly across the timeline
                          const startPercent = (entryIndex * 20) % 80;
                          const widthPercent = 15;
                          
                          return (
                            <Tooltip 
                              key={entryIndex} 
                              title={`${entry.driverName} - ${entry.activity} (${entry.duration} min)`} 
                              arrow
                            >
                              <div 
                                className={`absolute h-full text-white text-xs flex items-center justify-center overflow-hidden whitespace-nowrap ${
                                  entry.activity === 'load' ? 'bg-orange-400' :
                                  entry.activity === 'unload' ? 'bg-blue-400' :
                                  'bg-purple-400'
                                }`}
                                style={{ 
                                  left: `${startPercent}%`,
                                  width: `${widthPercent}%`
                                }}
                              >
                                {entry.duration}m
                              </div>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 shadow">
              <h3 className="font-bold text-lg mb-3">Door Utilization</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span>Inbound Doors:</span>
                  <span className="font-bold">{apiDockDoors.filter(d => d.group === 'inbound').length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Outbound Doors:</span>
                  <span className="font-bold">{apiDockDoors.filter(d => d.group === 'outbound').length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Currently Active:</span>
                  <span className="font-bold">{activeDoors.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Utilization Rate:</span>
                  <span className="font-bold">
                    {apiDockDoors.length > 0 ? Math.round((activeDoors.length / apiDockDoors.length) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 shadow">
              <h3 className="font-bold text-lg mb-3">Activity Types</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span>Load Operations:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().filter(e => e.activity === 'load').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Unload Operations:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().filter(e => e.activity === 'unload').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Combined Operations:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().filter(e => e.activity === 'both').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Total Operations:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().length}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 shadow">
              <h3 className="font-bold text-lg mb-3">Duration Stats</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span>Avg. Dock Time:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().length > 0 
                      ? Math.round(Object.values(apiDockHistory).flat().reduce((sum, entry) => sum + entry.duration, 0) / 
                          Object.values(apiDockHistory).flat().length)
                      : 0} min
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Max Dock Time:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().length > 0
                      ? Math.max(...Object.values(apiDockHistory).flat().map(e => e.duration))
                      : 0} min
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Min Dock Time:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().length > 0
                      ? Math.min(...Object.values(apiDockHistory).flat().map(e => e.duration))
                      : 0} min
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Door Details Modal */}
      {showDoorDetailsModal && selectedDoor && (
        <Modal
          open={showDoorDetailsModal}
          onClose={handleCloseDetailsModal}
          className="flex justify-center items-center"
        >
          <div className="w-96 bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={handleCloseDetailsModal}
            />
            <h1 className="text-xl font-bold mb-5">Door {selectedDoor.number} Details</h1>
            
            {/* <div className="mb-4">
              <h2 className="font-bold mb-2">Status</h2>
              <div className="flex gap-2">
                {(['empty', 'loading', 'loaded', 'unloading', 'unloaded'] as Status[]).map(status => (
                  <button
                    key={status}
                    className={twMerge(
                      'px-3 py-1 rounded text-sm',
                      selectedDoor.status === status 
                        ? `${getStatusColor(status)} text-white` 
                        : 'bg-gray-200 hover:bg-gray-300'
                    )}
                    onClick={() => handleStatusChange(status)}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div> */}
            
            <div className="mb-4">
              <h2 className="font-bold mb-2">Group</h2>
              <div className="flex gap-2">
                {(['inbound', 'outbound', 'unassigned'] as SpotGroup[]).map(group => (
                  <button
                    key={group}
                    className={twMerge(
                      'px-3 py-1 rounded text-sm',
                      selectedDoor.group === group 
                        ? `border-2 ${group === 'inbound' ? 'border-blue-500 bg-blue-100' : 
                            group === 'outbound' ? 'border-orange-500 bg-orange-100' : 
                            'border-gray-400 bg-gray-100'}` 
                        : 'bg-gray-200 hover:bg-gray-300 border border-gray-300'
                    )}
                    onClick={() => handleGroupChange(group)}
                  >
                    {group.charAt(0).toUpperCase() + group.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-bold">Current Assignment</h2>
              </div>
              
              {selectedDoor.assignedDriverId ? (
                <div className="p-3 border rounded bg-gray-50">
                  <p className="text-sm text-gray-600 mb-1">
                    Driver: {selectedDoor.driverName || "No Driver"}
                  </p>
                  {selectedDoor.trailerNumber && (
                    <p className="text-sm text-gray-600 mb-1">
                      Trailer: {selectedDoor.trailerNumber}
                    </p>
                  )}
                  {selectedDoor.shipmentNumber && (
                    <p className="text-sm text-gray-600">
                      Shipment: {selectedDoor.shipmentNumber}
                    </p>
                  )}

                  {selectedDoor.appointment_date && (
                    <p className="text-sm text-gray-600 mb-1">
                      Appointment Time: {selectedDoor.appointment_date.split('.')[0].slice(0, -3)}
                    </p>
                  )}

                  

                  {selectedDoor.carrier_name && (
                    <p className="text-sm text-gray-600 mb-1">
                      SCAC: {selectedDoor.carrier_name}
                    </p>
                  )}
                  
                  {/* <div className="flex justify-end mt-2">
                    <button 
                      className="text-red-600 hover:text-red-800 text-sm"
                      onClick={() => {
                        // TODO: Implement API call to clear assignment
                        console.log('Clear assignment');
                      }}
                    >
                      <Close fontSize="small" className="mr-1" />
                      End Assignment
                    </button>
                  </div> */}
                  
                </div>
              ) : (
                <p className="text-sm text-gray-500">No current assignment</p>
              )}
            </div>
            
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-bold">Note</h2>
                {!isEditingNote && (
                  <button
                    className="text-sky-600 text-sm hover:underline flex items-center"
                    onClick={() => setIsEditingNote(true)}
                  >
                    <Edit fontSize="small" className="mr-1" />
                    {selectedDoor.note ? 'Edit Note' : 'Add Note'}
                  </button>
                )}
              </div>
              
              {isEditingNote ? (
                <div>
                  <textarea
                    value={noteValue}
                    onChange={(e) => setNoteValue(e.target.value)}
                    placeholder="Enter a note about this dock door..."
                    className="w-full p-2 border rounded h-24 mb-2"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      className="text-gray-600 hover:text-gray-800 text-sm"
                      onClick={() => {
                        setNoteValue(selectedDoor.note || '');
                        setIsEditingNote(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="text-green-600 hover:text-green-800 text-sm"
                      onClick={handleSaveNote}
                    >
                      Save Note
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-3 border rounded bg-gray-50">
                  {selectedDoor.note ? (
                    <p className="text-sm text-gray-600">{selectedDoor.note}</p>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No note added</p>
                  )}
                </div>
              )}
            </div>
            
            <div className="mb-4">
              {/* <div className="flex items-center justify-between mb-2">
                <h2 className="font-bold">Scheduled Times</h2>
                <button
                  className="text-sky-600 text-sm hover:underline"
                  onClick={() => {
                    // TODO: Implement API call to add scheduled time
                    console.log('Add scheduled time');
                  }}
                >
                  + Add Time
                </button>
              </div>
               */}
             
            </div>
            
            <div className="mt-5 pt-3 border-t flex justify-end">
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded"
                onClick={handleCloseDetailsModal}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedHistoryDoor && (
        <Modal
          open={showHistoryModal}
          onClose={handleCloseHistoryModal}
          className="flex justify-center items-center"
        >
          <div className="w-[600px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={handleCloseHistoryModal}
            />
            <h1 className="text-xl font-bold mb-5">
              Door {selectedHistoryDoor.number} - Historical Activity
            </h1>
            
            <div className="mb-4">
              <ToggleButtonGroup
                value={timeRange}
                exclusive
                onChange={(event, newTimeRange) => {
                  if (newTimeRange !== null) {
                    setTimeRange(newTimeRange);
                  }
                }}
                size="small"
              >
                <ToggleButton value="today">Today</ToggleButton>
                <ToggleButton value="week">Past Week</ToggleButton>
                <ToggleButton value="month">Past Month</ToggleButton>
              </ToggleButtonGroup>
            </div>
            
            <div className="mb-4">
              <h2 className="font-bold mb-2">Activity Log</h2>
              
              {(apiDockHistory[selectedHistoryDoor.id] || []).length === 0 ? (
                <p className="text-sm text-gray-500">No historical activity for this door</p>
              ) : (
                <div className="space-y-3">
                  {(apiDockHistory[selectedHistoryDoor.id] || []).map((entry, index) => (
                    <div 
                      key={index} 
                      className={`p-3 border rounded ${
                        entry.activity === 'load' ? 'bg-orange-50 border-orange-200' :
                        entry.activity === 'unload' ? 'bg-blue-50 border-blue-200' :
                        'bg-purple-50 border-purple-200'
                      }`}
                    >
                      <div className="flex justify-between">
                        <span className="font-bold">{entry.driverName}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          entry.activity === 'load' ? 'bg-orange-100 text-orange-800' :
                          entry.activity === 'unload' ? 'bg-blue-100 text-blue-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {entry.activity.charAt(0).toUpperCase() + entry.activity.slice(1)}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1">Shipment: {entry.shipmentId}</p>
                      
                      <div className="flex justify-between text-xs text-gray-500 mt-2">
                        <span>Arrival: {entry.arrivalTime}</span>
                        <span>Departure: {entry.departureTime}</span>
                        <span>Duration: {entry.duration} min</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mt-5">
              <h2 className="font-bold mb-2">Statistics for Door {selectedHistoryDoor.number}</h2>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">Total Operations</p>
                  <p className="text-xl font-bold">{(apiDockHistory[selectedHistoryDoor.id] || []).length}</p>
                </div>
                
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">Avg. Duration</p>
                  <p className="text-xl font-bold">
                    {(apiDockHistory[selectedHistoryDoor.id] || []).length > 0
                      ? Math.round((apiDockHistory[selectedHistoryDoor.id] || []).reduce((sum, entry) => sum + entry.duration, 0) / 
                          (apiDockHistory[selectedHistoryDoor.id] || []).length)
                      : 0} min
                  </p>
                </div>
                
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">Utilization</p>
                  <p className="text-xl font-bold">
                    {(apiDockHistory[selectedHistoryDoor.id] || []).length > 0
                      ? Math.round(((apiDockHistory[selectedHistoryDoor.id] || []).reduce((sum, entry) => sum + entry.duration, 0) / 
                          (timeRange === 'today' ? 24 * 60 : timeRange === 'week' ? 7 * 24 * 60 : 30 * 24 * 60)) * 100)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-5 pt-3 border-t flex justify-end">
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded"
                onClick={handleCloseHistoryModal}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Yard Move Modal */}
      {showYardMoveModal && selectedDoor && (
        <Modal
          open={showYardMoveModal}
          onClose={() => setShowYardMoveModal(false)}
          className="flex justify-center items-center"
        >
          <div className="w-96 bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={() => setShowYardMoveModal(false)}
            />
            <h1 className="text-xl font-bold mb-5">Move to Yard - Door {selectedDoor.number}</h1>
            
            <div className="mb-4">
              <h2 className="font-bold mb-2">Select Yard Spot</h2>
              <Select
                options={yardSpots
                  .filter(spot => spot.status === 'available')
                  .map(spot => ({
                    value: spot.displayNumber,
                    label: spot.displayNumber
                  }))}
                value={selectedYardSpot ? {
                  value: selectedYardSpot,
                  label: yardSpots.find(s => s.displayNumber === selectedYardSpot)?.displayNumber || ''
                } : null}
                onChange={(option: any) => setSelectedYardSpot(option.value)}
                placeholder="Select yard spot"
                className="w-full"
              />
            </div>
            
            {/* PUID Field - only show editable field if not 'unloading complete' */}
            {selectedDoor.current_status !== 'unloading complete' && (
              <div className="mb-4">
                <h2 className="font-bold mb-2">PUID</h2>
                <input
                  type="text"
                  value={selectedDoor.puid || ''}
                  onChange={(e) => {
                    // Create a copy of the door with the updated PUID
                    const updatedDoor = {...selectedDoor};
                    // @ts-ignore - add PUID property dynamically
                    updatedDoor.puid = e.target.value;
                    setSelectedDoor(updatedDoor);
                  }}
                  placeholder="Enter PUID"
                  className="w-full p-2 border rounded"
                />
              </div>
            )}
            
            <div className="mt-5 pt-3 border-t flex justify-end gap-2">
              <button
                className="bg-gray-500 hover:bg-gray-400 text-white p-2 rounded"
                onClick={() => setShowYardMoveModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-purple-600 hover:bg-purple-500 text-white p-2 rounded"
                onClick={async () => {
                  if (!selectedYardSpot) return;
                  try {
                    const payload = {
                      bol_id: selectedDoor.bol_id,
                      yard_spot: selectedYardSpot,
                      puid: selectedDoor.puid || null,
                      trailer_number: selectedDoor.trailerNumber || null
                    };
                    
                    // Show loading indicator or disable button here if desired
                    
                    const response = await axios.post(
                      `${process.env.REACT_APP_WRITE_API_URL}/api/v1/admin/drivers/move_to_yard`, 
                      payload
                    );
                    
                    console.log('Move to yard response:', response.data);
                    
                    // Success message
                    alert(response.data.message || 'Trailer successfully moved to yard spot');
                    
                    setTimeInterval(prev => prev + 1); // Refresh data
                    setShowYardMoveModal(false);
                  } catch (error: any) {
                    console.error('Failed to move to yard:', error);
                    
                    // Show error message from API if available
                    if (error.response && error.response.data && error.response.data.error) {
                      alert(`Error: ${error.response.data.error}`);
                    } else {
                      alert('Failed to move trailer to yard spot. Please try again.');
                    }
                  }
                }}
              >
                Confirm Move
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Configuration Modal */}
      {showConfigModal && (
        <Modal
          open={showConfigModal}
          onClose={() => setShowConfigModal(false)}
          className="flex justify-center items-center"
        >
          <div className="w-[700px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={() => setShowConfigModal(false)}
            />
            <h1 className="text-xl font-bold mb-5">Dock Management Configuration</h1>
            
            <div className="mb-6">
              <Tabs 
                value={configModalTab} 
                onChange={handleConfigModalTabChange} 
                className="mb-4"
              >
                <Tab label="Indicator Colors" />
                <Tab label="Card Colors" />
                <Tab label="Thresholds" />
              </Tabs>
              
              {/* Content for tab 0 - Indicator Colors */}
              {configModalTab === 0 && (
                <>
                  <h2 className="font-bold text-lg mb-3">Timer Indicator Colors</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure the colors used for different time statuses in the dock management display.
                  </p>
                  
                  {/* MDT Colors */}
                  <div className="mb-4">
                    <h3 className="font-medium mb-2">MDT Colors</h3>
                    <div className="grid grid-cols-3 gap-4 mb-2">
                      <div>
                        <label className="block text-sm font-medium mb-1">Good Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.mdt.good}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            mdt: {...colorConfig.mdt, good: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.mdt.warning}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            mdt: {...colorConfig.mdt, warning: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.mdt.critical}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            mdt: {...colorConfig.mdt, critical: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Text Color</label>
                      <div className="flex items-center">
                        <input 
                          type="color" 
                          value={colorConfig.mdt.textColor}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            mdt: {...colorConfig.mdt, textColor: e.target.value}
                          })}
                          className="w-32 h-10 cursor-pointer mr-2"
                        />
                        <div className="flex-1 p-2 border rounded-md flex">
                          <div 
                            className="p-2 flex-1 text-center" 
                            style={{ 
                              backgroundColor: colorConfig.mdt.good,
                              color: colorConfig.mdt.textColor
                            }}
                          >
                            Text on good
                          </div>
                          <div 
                            className="p-2 flex-1 text-center" 
                            style={{ 
                              backgroundColor: colorConfig.mdt.warning,
                              color: colorConfig.mdt.textColor
                            }}
                          >
                            Text on warning
                          </div>
                          <div 
                            className="p-2 flex-1 text-center" 
                            style={{ 
                              backgroundColor: colorConfig.mdt.critical,
                              color: colorConfig.mdt.textColor
                            }}
                          >
                            Text on critical
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Dock Timer Colors - Add similar text color picker */}
                  <div className="mb-4">
                    <h3 className="font-medium mb-2">Dock Timer Colors</h3>
                    <div className="grid grid-cols-3 gap-4 mb-2">
                      <div>
                        <label className="block text-sm font-medium mb-1">Good Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.dockTimer.good}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            dockTimer: {...colorConfig.dockTimer, good: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.dockTimer.warning}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            dockTimer: {...colorConfig.dockTimer, warning: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.dockTimer.critical}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            dockTimer: {...colorConfig.dockTimer, critical: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Text Color</label>
                      <div className="flex items-center">
                        <input 
                          type="color" 
                          value={colorConfig.dockTimer.textColor}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            dockTimer: {...colorConfig.dockTimer, textColor: e.target.value}
                          })}
                          className="w-32 h-10 cursor-pointer mr-2"
                        />
                        <div className="flex-1 p-2 border rounded-md flex">
                          <div 
                            className="p-2 flex-1 text-center" 
                            style={{ 
                              backgroundColor: colorConfig.dockTimer.good,
                              color: colorConfig.dockTimer.textColor
                            }}
                          >
                            Text on good
                          </div>
                          <div 
                            className="p-2 flex-1 text-center" 
                            style={{ 
                              backgroundColor: colorConfig.dockTimer.warning,
                              color: colorConfig.dockTimer.textColor
                            }}
                          >
                            Text on warning
                          </div>
                          <div 
                            className="p-2 flex-1 text-center" 
                            style={{ 
                              backgroundColor: colorConfig.dockTimer.critical,
                              color: colorConfig.dockTimer.textColor
                            }}
                          >
                            Text on critical
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Detention Time Colors - Add similar text color picker */}
                  <div className="mb-4">
                    <h3 className="font-medium mb-2">Detention Time Colors</h3>
                    <div className="grid grid-cols-3 gap-4 mb-2">
                      <div>
                        <label className="block text-sm font-medium mb-1">Good Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.detention.good}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            detention: {...colorConfig.detention, good: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.detention.warning}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            detention: {...colorConfig.detention, warning: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.detention.critical}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            detention: {...colorConfig.detention, critical: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Text Color</label>
                      <div className="flex items-center">
                        <input 
                          type="color" 
                          value={colorConfig.detention.textColor}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            detention: {...colorConfig.detention, textColor: e.target.value}
                          })}
                          className="w-32 h-10 cursor-pointer mr-2"
                        />
                        <div className="flex-1 p-2 border rounded-md flex">
                          <div 
                            className="p-2 flex-1 text-center" 
                            style={{ 
                              backgroundColor: colorConfig.detention.good,
                              color: colorConfig.detention.textColor
                            }}
                          >
                            Text on good
                          </div>
                          <div 
                            className="p-2 flex-1 text-center" 
                            style={{ 
                              backgroundColor: colorConfig.detention.warning,
                              color: colorConfig.detention.textColor
                            }}
                          >
                            Text on warning
                          </div>
                          <div 
                            className="p-2 flex-1 text-center" 
                            style={{ 
                              backgroundColor: colorConfig.detention.critical,
                              color: colorConfig.detention.textColor
                            }}
                          >
                            Text on critical
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* Content for tab 1 - Card Colors */}
              {configModalTab === 1 && (
                <>
                  <h2 className="font-bold text-lg mb-3">Card Background Colors</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure how dock door cards change color based on timer statuses.
                  </p>
                  
                  <div className="mb-6">
                    {/* Default State - Single Row */}
                    <div className="border-b pb-3 mb-3">
                      <h3 className="font-medium mb-3">Default State</h3>
                        <div className="flex items-center">
                        <label className="w-32 text-sm font-medium">All Good:</label>
                          <input 
                          type="color" 
                          value={colorConfig.card.default}
                          onChange={(e) => setColorConfig({
                                ...colorConfig,
                            card: {...colorConfig.card, default: e.target.value}
                          })}
                          className="w-12 h-8 cursor-pointer mr-3"
                        />
                        <div className="flex-1 p-2 border rounded-md" style={{ backgroundColor: colorConfig.card.default }}>
                          <span className="text-sm">No warnings or critical indicators</span>
                        </div>
                      </div>
                      </div>

                    {/* Warning States - Compact Grid */}
                    <div className="border-b pb-3 mb-3">
                      <h3 className="font-medium mb-3">Warning States</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { key: 'oneWarning', label: 'One Warning:', desc: 'One indicator in warning state' },
                          { key: 'twoWarnings', label: 'Two Warnings:', desc: 'Two indicators in warning state' },
                          { key: 'threeWarnings', label: 'Three Warnings:', desc: 'All indicators in warning state' }
                        ].map(item => (
                          <div key={item.key} className="flex items-center">
                            <label className="w-32 text-sm font-medium">{item.label}</label>
                            <input 
                              type="color" 
                              value={colorConfig.card[item.key as keyof typeof colorConfig.card]}
                              onChange={(e) => setColorConfig({
                                ...colorConfig,
                                card: {
                                  ...colorConfig.card, 
                                  [item.key]: e.target.value
                                }
                              })}
                              className="w-12 h-8 cursor-pointer mr-3"
                          />
                          <div 
                              className="flex-1 p-2 border rounded-md" 
                              style={{ backgroundColor: colorConfig.card[item.key as keyof typeof colorConfig.card] }}
                            >
                              <span className="text-sm">{item.desc}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      </div>

                    {/* Critical States - Compact Grid */}
                    <div className="border-b pb-3 mb-3">
                      <h3 className="font-medium mb-3">Critical States</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { key: 'oneCritical', label: 'One Critical:', desc: 'One indicator in critical state' },
                          { key: 'twoCriticals', label: 'Two Criticals:', desc: 'Two indicators in critical state' },
                          { key: 'threeCriticals', label: 'Three Criticals:', desc: 'All indicators in critical state' }
                        ].map(item => (
                          <div key={item.key} className="flex items-center">
                            <label className="w-32 text-sm font-medium">{item.label}</label>
                            <input 
                              type="color" 
                              value={colorConfig.card[item.key as keyof typeof colorConfig.card]}
                              onChange={(e) => setColorConfig({
                                ...colorConfig,
                                card: {
                                  ...colorConfig.card, 
                                  [item.key]: e.target.value
                                }
                              })}
                              className="w-12 h-8 cursor-pointer mr-3"
                            />
                            <div 
                              className="flex-1 p-2 border rounded-md" 
                              style={{ backgroundColor: colorConfig.card[item.key as keyof typeof colorConfig.card] }}
                            >
                              <span className="text-sm">{item.desc}</span>
                        </div>
                      </div>
                        ))}
                    </div>
                  </div>
                  
                    {/* Mixed State - Single Row */}
                    <div>
                      <h3 className="font-medium mb-3">Mixed State</h3>
                      <div className="flex items-center">
                        <label className="w-32 text-sm font-medium">Mixed:</label>
                        <input 
                          type="color" 
                          value={colorConfig.card.mixed}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            card: {...colorConfig.card, mixed: e.target.value}
                          })}
                          className="w-12 h-8 cursor-pointer mr-3"
                        />
                        <div className="flex-1 p-2 border rounded-md" style={{ backgroundColor: colorConfig.card.mixed }}>
                          <span className="text-sm">Mix of warning and critical indicators</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Priority Rules Description */}
                  <div className="bg-gray-50 p-3 rounded-md text-sm">
                    <h4 className="font-medium mb-1">How Card Colors Work:</h4>
                    <p className="mb-2">Cards change color based on the most severe indicators:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Critical indicators take priority over warnings</li>
                      <li>More indicators of the same severity = darker color</li>
                      <li>Mixed state is used when both warning and critical indicators are present</li>
                    </ul>
                  </div>
                </>
              )}
              
              {/* Content for tab 2 - Thresholds */}
              {configModalTab === 2 && (
                <>
                  <h2 className="font-bold text-lg mb-3">Time Thresholds</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure when timers should switch to warning or critical status (in minutes).
                  </p>
                  
                  {/* MDT Thresholds */}
                  <div className="border-b pb-4 mb-4">
                    <h3 className="font-medium mb-3">MDT Thresholds</h3>
                    <div className="grid grid-cols-1 gap-4 mb-2">
                      <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                        <strong>Note:</strong> MDT is a countdown timer. Lower values indicate less time remaining.
                        <ul className="list-disc pl-5 mt-1">
                          <li>Warning: When time drops below this threshold</li>
                          <li>Critical: When time drops below this threshold</li>
                        </ul>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.mdt.warningThreshold}
                            onChange={(e) => {
                              const newWarning = parseInt(e.target.value) || 10;
                              setColorConfig({
                                ...colorConfig,
                                mdt: {
                                  ...colorConfig.mdt,
                                  warningThreshold: newWarning
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.mdt.warning }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns yellow when below this many minutes
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.mdt.criticalThreshold}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 5;
                              setColorConfig({
                                ...colorConfig,
                                mdt: {
                                  ...colorConfig.mdt, 
                                  criticalThreshold: value
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.mdt.critical }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns red when below this many minutes
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Dock Timer Thresholds */}
                  <div className="border-b pb-4 mb-4">
                    <h3 className="font-medium mb-3">Dock Timer Thresholds</h3>
                    <div className="grid grid-cols-1 gap-4 mb-2">
                      <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                        <strong>Note:</strong> Dock Timer counts up. Higher values indicate more time spent at dock.
                        <ul className="list-disc pl-5 mt-1">
                          <li>Warning: When time exceeds this threshold</li>
                          <li>Critical: When time exceeds this threshold</li>
                        </ul>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.dockTimer.warningThreshold}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 10;
                              setColorConfig({
                                ...colorConfig,
                                dockTimer: {
                                  ...colorConfig.dockTimer, 
                                  warningThreshold: value
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.dockTimer.warning }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns yellow when exceeding this many minutes
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.dockTimer.criticalThreshold}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 30;
                              setColorConfig({
                                ...colorConfig,
                                dockTimer: {
                                  ...colorConfig.dockTimer, 
                                  criticalThreshold: value
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.dockTimer.critical }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns red when exceeding this many minutes
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Detention Time Thresholds */}
                  <div>
                    <h3 className="font-medium mb-3">Detention Time Thresholds</h3>
                    <div className="grid grid-cols-1 gap-4 mb-2">
                      <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                        <strong>Note:</strong> Detention Timer is a countdown timer. Lower values indicate less time remaining.
                        <ul className="list-disc pl-5 mt-1">
                          <li>Warning: When time drops below this threshold</li>
                          <li>Critical: When time drops below this threshold</li>
                        </ul>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.detention.warningThreshold}
                            onChange={(e) => {
                              const newWarning = parseInt(e.target.value) || 10;
                              setColorConfig({
                                ...colorConfig,
                                detention: {
                                  ...colorConfig.detention,
                                  warningThreshold: newWarning
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.detention.warning }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns yellow when below this many minutes
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.detention.criticalThreshold}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 5;
                              setColorConfig({
                                ...colorConfig,
                                detention: {
                                  ...colorConfig.detention, 
                                  criticalThreshold: value
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.detention.critical }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns red when below this many minutes
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Thresholds explanation */}
                  <div className="bg-gray-50 p-3 rounded-md text-sm mt-6">
                    <h4 className="font-medium mb-1">About Thresholds:</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      <li><strong>MDT & Detention:</strong> Countdown timers - warnings happen as time gets low</li>
                      <li><strong>Dock Timer:</strong> Count-up timer - warnings happen as time gets high</li>
                      <li>For optimal results, MDT/Detention warning should be higher than critical</li>
                      <li>For optimal results, Dock Timer critical should be higher than warning</li>
                      <li>Times in the "good" range display with the "good" color</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
            
            <div className="mt-5 pt-3 border-t flex justify-end gap-2">
              <button
                className="bg-gray-500 hover:bg-gray-400 text-white p-2 rounded"
                onClick={() => setShowConfigModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-500 text-white p-2 rounded"
                onClick={saveConfiguration}
              >
                Save Configuration
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DockManagement; 